// Companion/blocks/QuestionBlock.tsx
import { QuestionBlockBody } from './bodies/QuestionBlockBody/QuestionBlockBody';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type {
  ChatQuestionBlockLimits,
  ChatQuestionBlockPayload,
} from '@/types/chatBlocks';
import type { ChatAnchorInput } from '@/types/chatEnvelope';
import type { ChatQuestionFollowup } from './chatQuestionFollowup';
import { useBlockAnchor } from './useBlockAnchor';
import { useQuestionStudyBadges } from './useQuestionStudyBadges';

/**
 * A practice question served into the thread.
 *
 * The body is the ported `QuestionBlockBody`, unchanged — which is what makes
 * an answer given here count once: it submits through
 * `submitChatVignetteAnswer` (`source: 'chat'`) to the same
 * `/api/v5/qbank/vignettes/:id/answer` endpoint QBank writes to, so opening the
 * question in the full UI later finds that response row rather than a second
 * one. That component also owns the free-tier gate, refusing the answer and
 * offering the upgrade modal at the daily cap.
 *
 * This adapter's whole job is vocabulary: the new camelCase payload becomes the
 * snake_case `item` and anchor the ported component reads, and the anchor it
 * reports back becomes ORA-449's `ChatAnchorInput`.
 */

/** The payload's `options` in the shape `QuestionBlockBody` reads. */
const toLegacyOptions = (payload: ChatQuestionBlockPayload) =>
  payload.options.map((option) => ({
    ...(option.letter ? { letter: option.letter } : {}),
    text: option.text,
    explanation_main: option.explanation ?? '',
    ...(option.isCorrect === undefined ? {} : { is_correct: option.isCorrect }),
  }));

/** The payload in the `item` shape the ported component reads. Shared with the
 * study-set block, which serves the same questions inside a set. */
export const toLegacyItem = (payload: ChatQuestionBlockPayload) => ({
  id: payload.vignetteId,
  variant_id: payload.variantId ?? '',
  question_stem: payload.stem,
  correct_letter: payload.correctLetter ?? '',
  correct_explanation: payload.explanation ?? '',
  options: toLegacyOptions(payload),
  // The stored marks (ORA-822), only when the payload carries them.
  ...(payload.questionHighlights !== undefined
    ? { question_highlights: payload.questionHighlights }
    : {}),
  ...(payload.answerCrossouts !== undefined
    ? { answer_crossouts: payload.answerCrossouts }
    : {}),
});

/**
 * An answer the student already gave, in the anchor shape the ported component
 * seeds its revealed state from. Null when they have not answered, which is the
 * ordinary case for a freshly served question.
 */
export const toSeedAnchor = (payload: ChatQuestionBlockPayload) =>
  payload.selectedLetter
    ? {
        type: 'vignette',
        id: payload.vignetteId,
        vignette: {
          revealed: true,
          user_selected_letter: payload.selectedLetter,
          variant_id: payload.variantId ?? null,
        },
      }
    : undefined;

/** The daily-QBank line a free user sees above a served question. */
export const QuestionBlockLimits = ({
  limits,
}: {
  limits: ChatQuestionBlockLimits;
}) => {
  const { qbankRemaining, qbankLimit } = limits;
  if (qbankRemaining === null) return null;

  if (qbankRemaining <= 0) {
    return (
      <p className="m-0 mb-2 text-xs text-fg-secondary">
        You have used your free QBank questions for today.
      </p>
    );
  }

  return (
    <p className="m-0 mb-2 text-xs text-fg-secondary">
      {qbankRemaining} of {qbankLimit ?? qbankRemaining} free QBank{' '}
      {qbankLimit === 1 ? 'question' : 'questions'} left today.
    </p>
  );
};

interface QuestionBlockProps {
  payload: ChatQuestionBlockPayload;
  /** The served block itself; its ids are what the badges add content against. */
  block: ChatBlock;
  isReadOnly: boolean;
  onAnsweredChange?: (
    answered: boolean,
    question?: ChatQuestionFollowup
  ) => void;
  onBeforeContentExpand?: () => void;
}

const QuestionBlock = ({
  payload,
  block,
  isReadOnly,
  onAnsweredChange,
  onBeforeContentExpand,
}: QuestionBlockProps) => {
  const { claim } = useBlockAnchor(isReadOnly);

  // The badges under a miss: the variant it scheduled and the cards it assigned
  // (ORA-546/547), studied here in the thread (ORA-831).
  const { badgePropsFor } = useQuestionStudyBadges({
    block,
    isReadOnly,
    source: { kind: 'question' },
  });

  return (
    <>
      {payload.limits ? <QuestionBlockLimits limits={payload.limits} /> : null}
      <QuestionBlockBody
        item={toLegacyItem(payload)}
        anchor={toSeedAnchor(payload)}
        lectureId={payload.lectureId ?? undefined}
        onBeforeContentExpand={onBeforeContentExpand}
        onAnswerConfirmed={(firstAttemptCorrect) =>
          onAnsweredChange?.(true, {
            blockId: block.id,
            anchor: {
              type: 'question',
              id: payload.vignetteId,
              ...(payload.variantId ? { variantId: payload.variantId } : {}),
            },
            firstAttemptCorrect,
          })
        }
        {...badgePropsFor({ vignetteId: payload.vignetteId })}
        onAnchorChange={(anchor) => {
          const vignette = (anchor?.vignette ?? {}) as Record<string, unknown>;
          const revealed = vignette.revealed === true;

          claim({
            type: 'question',
            vignetteId: payload.vignetteId,
            variantId: payload.variantId ?? null,
            revealed,
          } satisfies ChatAnchorInput);

          // `revealed: true` is optimistic and precedes the grading write.
          // Only a rollback can be reported from the anchor; confirmation has
          // its own callback above and fires after the write succeeds.
          if (!revealed) onAnsweredChange?.(false);
        }}
      />
    </>
  );
};

export default QuestionBlock;
